import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import API from '../api';

function ClientDashboard() {
  const { userInfo, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [adminUser, setAdminUser] = useState(null);
  const [activeTab, setActiveTab] = useState('projects');

useEffect(() => {
    fetchMyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchMyData = async () => {
    try {
      const { data: allProjects } = await API.get('/projects');
      const { data: allInvoices } = await API.get('/invoices');
      const { data: allFiles } = await API.get('/files');
      const { data: contacts } = await API.get('/messages/contacts');

      const myProjects = allProjects.filter(p => p.client?.email === userInfo?.email);
      const myInvoices = allInvoices.filter(i => i.client?.email === userInfo?.email);
      const myFiles = allFiles.filter(f => f.client?.email === userInfo?.email);

      const admin = contacts.find(c => c.role === 'admin');
      setAdminUser(admin);

      if (admin) {
        const { data: msgs } = await API.get(`/messages/${admin._id}`);
        setMessages(msgs);
      }

      setProjects(myProjects);
      setInvoices(myInvoices);
      setFiles(myFiles);
    } catch (error) {
      console.error('Error fetching data');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !adminUser) return;
    try {
      await API.post('/messages', {
        receiver: adminUser._id,
        content: newMessage
      });
      setNewMessage('');
      const { data: msgs } = await API.get(`/messages/${adminUser._id}`);
      setMessages(msgs);
    } catch (error) {
      console.error('Error sending message');
    }
  };

  const statusColor = {
    'pending': '#f59e0b',
    'in-progress': '#3b82f6',
    'review': '#8b5cf6',
    'completed': '#10b981'
  };

  return (
    <div style={styles.container}>
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <h2 style={styles.logoText}>ClientPortal</h2>
          <p style={styles.logoSub}>Client Panel</p>
        </div>
        <nav style={styles.nav}>
          <button
            style={{
              ...styles.navItem,
              backgroundColor: activeTab === 'projects' ? '#ffffff15' : 'transparent',
              color: activeTab === 'projects' ? 'white' : '#a0aec0'
            }}
            onClick={() => setActiveTab('projects')}
          >
            My Projects
          </button>
          <button
            style={{
              ...styles.navItem,
              backgroundColor: activeTab === 'invoices' ? '#ffffff15' : 'transparent',
              color: activeTab === 'invoices' ? 'white' : '#a0aec0'
            }}
            onClick={() => setActiveTab('invoices')}
          >
            My Invoices
          </button>
          <button
            style={{
              ...styles.navItem,
              backgroundColor: activeTab === 'files' ? '#ffffff15' : 'transparent',
              color: activeTab === 'files' ? 'white' : '#a0aec0'
            }}
            onClick={() => setActiveTab('files')}
          >
            My Files
          </button>
          <button
            style={{
              ...styles.navItem,
              backgroundColor: activeTab === 'messages' ? '#ffffff15' : 'transparent',
              color: activeTab === 'messages' ? 'white' : '#a0aec0'
            }}
            onClick={() => setActiveTab('messages')}
          >
            Messages
          </button>
        </nav>
        <button style={styles.logoutBtn} onClick={handleLogout}>
          Logout
        </button>
      </div>

      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.heading}>
              {activeTab === 'projects' ? 'My Projects' :
               activeTab === 'invoices' ? 'My Invoices' :
               activeTab === 'files' ? 'My Files' : 'Messages'}
            </h1>
            <p style={styles.subheading}>Welcome, {userInfo?.name}!</p>
          </div>
        </div>

        {activeTab === 'projects' && (
          <div>
            {projects.length === 0 ? (
              <div style={styles.empty}>No projects assigned yet.</div>
            ) : (
              <div style={styles.grid}>
                {projects.map((project) => (
                  <div key={project._id} style={styles.card}>
                    <div style={styles.cardHeader}>
                      <h3 style={styles.projectTitle}>{project.title}</h3>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: statusColor[project.status] + '20',
                        color: statusColor[project.status]
                      }}>
                        {project.status}
                      </span>
                    </div>
                    <p style={styles.description}>
                      {project.description || 'No description'}
                    </p>
                    {project.deadline && (
                      <p style={styles.meta}>
                        Due: {new Date(project.deadline).toLocaleDateString()}
                      </p>
                    )}
                    <div style={styles.progressLabel}>
                      <span>Progress</span>
                      <span>{project.progress}%</span>
                    </div>
                    <div style={styles.progressBar}>
                      <div style={{
                        ...styles.progressFill,
                        width: `${project.progress}%`,
                        backgroundColor: statusColor[project.status]
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div style={styles.tableContainer}>
            {invoices.length === 0 ? (
              <div style={styles.empty}>No invoices yet.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.th}>Invoice #</th>
                    <th style={styles.th}>Total</th>
                    <th style={styles.th}>Due Date</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice._id} style={styles.tableRow}>
                      <td style={styles.td}>{invoice.invoiceNumber}</td>
                      <td style={styles.td}>${invoice.total?.toFixed(2)}</td>
                      <td style={styles.td}>
                        {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '-'}
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: invoice.status === 'paid' ? '#d1fae5' : invoice.status === 'overdue' ? '#fee2e2' : '#fef3c7',
                          color: invoice.status === 'paid' ? '#065f46' : invoice.status === 'overdue' ? '#991b1b' : '#d97706'
                        }}>
                          {invoice.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          <div style={styles.tableContainer}>
            {files.length === 0 ? (
              <div style={styles.empty}>No files shared yet.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.th}>File Name</th>
                    <th style={styles.th}>Size</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => (
                    <tr key={file._id} style={styles.tableRow}>
                      <td style={styles.td}>{file.originalName}</td>
                      <td style={styles.td}>
                        {file.fileSize ? `${(file.fileSize / 1024).toFixed(1)} KB` : '-'}
                      </td>
                      <td style={styles.td}>
                        {new Date(file.createdAt).toLocaleDateString()}
                      </td>
                      <td style={styles.td}>
                        <a
                         href={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/uploads/${file.filename}`}
                          target="_blank"
                          rel="noreferrer"
                          style={styles.downloadLink}
                        >
                          Download
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'messages' && (
          <div style={styles.chatBox}>
            <div style={styles.messagesArea}>
              {messages.length === 0 ? (
                <p style={styles.noMessages}>No messages yet. Send a message to your admin!</p>
              ) : (
                messages.map((msg) => {
                  const isMe = msg.sender.name === userInfo.name;
                  return (
                    <div key={msg._id} style={{
                      ...styles.bubbleWrapper,
                      justifyContent: isMe ? 'flex-end' : 'flex-start'
                    }}>
                      <div style={{
                        ...styles.bubble,
                        backgroundColor: isMe ? '#4f46e5' : '#f1f5f9',
                        color: isMe ? 'white' : '#1a1a2e'
                      }}>
                        <p style={styles.bubbleText}>{msg.content}</p>
                        <p style={{
                          ...styles.bubbleTime,
                          color: isMe ? '#c7d2fe' : '#94a3b8'
                        }}>
                          {new Date(msg.createdAt).toLocaleTimeString([], {
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div style={styles.inputArea}>
              <input
                style={styles.msgInput}
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <button style={styles.sendBtn} onClick={handleSendMessage}>
                Send
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  container: { display: 'flex', minHeight: '100vh', backgroundColor: '#f7f8fc' },
  sidebar: {
    width: '250px', minHeight: '100vh', backgroundColor: '#1a1a2e',
    display: 'flex', flexDirection: 'column', position: 'fixed', left: 0, top: 0
  },
  logo: { padding: '30px 24px', borderBottom: '1px solid #ffffff15' },
  logoText: { color: 'white', margin: '0', fontSize: '22px', fontWeight: '700' },
  logoSub: { color: '#a0aec0', margin: '4px 0 0 0', fontSize: '12px' },
  nav: { display: 'flex', flexDirection: 'column', padding: '20px 0', flex: 1 },
  navItem: {
    padding: '12px 24px', border: 'none', cursor: 'pointer',
    fontSize: '15px', textAlign: 'left', borderRadius: '0'
  },
  logoutBtn: {
    margin: '20px', padding: '12px', backgroundColor: '#e53e3e',
    color: 'white', border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600'
  },
  main: { marginLeft: '250px', flex: 1, padding: '30px' },
  header: { marginBottom: '30px' },
  heading: { margin: '0', fontSize: '28px', fontWeight: '700', color: '#1a1a2e' },
  subheading: { margin: '4px 0 0 0', color: '#718096', fontSize: '15px' },
  empty: {
    backgroundColor: 'white', borderRadius: '12px', padding: '60px',
    textAlign: 'center', color: '#718096', boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  projectTitle: { margin: '0', fontSize: '18px', fontWeight: '700', color: '#1a1a2e' },
  badge: { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  description: { margin: '0 0 12px 0', color: '#718096', fontSize: '14px' },
  meta: { margin: '0 0 12px 0', fontSize: '14px', color: '#4a5568' },
  progressLabel: { display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#718096', marginBottom: '6px' },
  progressBar: { height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: '4px', transition: 'width 0.3s ease' },
  tableContainer: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f7f8fc' },
  th: { padding: '14px 20px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#718096', borderBottom: '1px solid #e2e8f0' },
  tableRow: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '14px 20px', fontSize: '14px', color: '#4a5568' },
  downloadLink: { padding: '6px 12px', backgroundColor: '#dbeafe', color: '#1d4ed8', borderRadius: '6px', fontSize: '13px', textDecoration: 'none' },
  chatBox: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', height: '65vh' },
  messagesArea: { flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '8px' },
  noMessages: { textAlign: 'center', color: '#718096', marginTop: '40px' },
  bubbleWrapper: { display: 'flex' },
  bubble: { maxWidth: '60%', padding: '10px 14px', borderRadius: '12px', wordBreak: 'break-word' },
  bubbleText: { margin: '0', fontSize: '14px', lineHeight: '1.5' },
  bubbleTime: { margin: '4px 0 0 0', fontSize: '11px' },
  inputArea: { display: 'flex', gap: '8px', padding: '16px', borderTop: '1px solid #e2e8f0', alignItems: 'center' },
  msgInput: { flex: 1, padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' },
  sendBtn: { padding: '10px 20px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' }
};

export default ClientDashboard;